<script setup>
import { computed } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  weeklyData: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const hasData = computed(() => props.weeklyData && props.weeklyData.length > 0);

// Calculate totals across all weeks in the table
const totals = computed(() => {
  if (!hasData.value) return null;

  const sum = (key) => props.weeklyData.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

  const totalTickets = sum('total_tickets');
  const totalEmailTickets = sum('total_email_tickets');
  const resolvedEmailTickets = sum('resolved_email_tickets');
  const emailResolutionRate = totalEmailTickets > 0 ? Number(((resolvedEmailTickets / totalEmailTickets) * 100).toFixed(1)) : 0.0;

  const totalLiveChats = sum('total_live_chats');
  const missedLiveChats = sum('missed_live_chats');

  const totalInboundCalls = sum('total_inbound_calls');
  const completedInboundCalls = sum('completed_inbound_calls');
  const inboundCompletionRate = totalInboundCalls > 0 ? Number(((completedInboundCalls / totalInboundCalls) * 100).toFixed(1)) : 0.0;
  const voicemails = sum('voicemails');
  const abandonedCalls = sum('abandoned_calls');
  const abandonedCallRate = totalInboundCalls > 0 ? Number(((abandonedCalls / totalInboundCalls) * 100).toFixed(1)) : 0.0;

  const totalCallsBH = sum('total_calls_business_hours');
  const answeredBH = sum('answered_business_hours');
  const bhCompletionRate = totalCallsBH > 0 ? Number(((answeredBH / totalCallsBH) * 100).toFixed(1)) : 0.0;
  const abandonedBH = sum('abandoned_business_hours');
  const bhAbandonmentRate = totalCallsBH > 0 ? Number(((abandonedBH / totalCallsBH) * 100).toFixed(1)) : 0.0;

  const totalCallsNBH = sum('total_calls_non_business_hours');
  const abandonedNBH = sum('abandoned_non_business_hours');
  const nbhAbandonmentRate = totalCallsNBH > 0 ? Number(((abandonedNBH / totalCallsNBH) * 100).toFixed(1)) : 0.0;

  const totalTbpOrders = sum('total_tbp_orders');
  const totalCallsMade = sum('total_calls_made');

  return {
    totalTickets,
    totalEmailTickets,
    resolvedEmailTickets,
    emailResolutionRate: totalEmailTickets > 0 ? Number(((resolvedEmailTickets / totalEmailTickets) * 100).toFixed(1)) : 0.0,
    totalLiveChats,
    missedLiveChats,
    totalInboundCalls,
    completedInboundCalls,
    inboundCompletionRate,
    voicemails,
    abandonedCalls,
    abandonedCallRate,
    totalCallsBH,
    answeredBH,
    bhCompletionRate,
    abandonedBH,
    bhAbandonmentRate,
    totalCallsNBH,
    abandonedNBH,
    nbhAbandonmentRate,
    totalTbpOrders,
    totalCallsMade,
  };
});
</script>

<template>
  <div class="bg-n-surface-1 border border-n-strong rounded-xl shadow-sm overflow-hidden flex flex-col">
    <!-- Table Header / Title -->
    <div class="px-5 py-4 border-b border-n-slate-4 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Icon icon="i-lucide-table-properties" class="w-5 h-5 text-n-brand" />
        <h3 class="text-base font-semibold text-n-slate-12 m-0">
          {{ $t('REPORT.KPI_TABLE.TITLE') }}
        </h3>
      </div>
      <span class="text-xs text-n-slate-11">
        {{ $t('REPORT.KPI_TABLE.WEEKS_DISPLAYED', { count: weeklyData.length }) }}
      </span>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center p-16 gap-3">
      <Spinner class="w-8 h-8 text-n-brand" />
      <span class="text-xs text-n-slate-11 font-medium">{{ $t('REPORT.KPI_TABLE.LOADING_DATA') }}</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="!hasData" class="flex flex-col items-center justify-center p-16 gap-3 text-center">
      <div class="w-12 h-12 rounded-full bg-n-slate-3 flex items-center justify-center text-n-slate-10">
        <Icon icon="i-lucide-calendar-x-2" class="w-6 h-6" />
      </div>
      <h4 class="text-sm font-semibold text-n-slate-12 m-0">{{ $t('REPORT.KPI_TABLE.NO_DATA_TITLE') }}</h4>
      <p class="text-xs text-n-slate-11 max-w-sm m-0">
        {{ $t('REPORT.KPI_TABLE.NO_DATA_DESCRIPTION') }}
      </p>
    </div>

    <!-- Responsive Table with Grouped Header Structure -->
    <div v-else class="overflow-x-auto w-full">
      <table class="w-full text-xs text-left border-collapse min-w-[1700px]">
        <!-- Super Headers (Logical Groupings) -->
        <thead>
          <tr class="bg-n-slate-3 text-n-slate-11 border-b border-n-slate-5 text-[11px] font-semibold uppercase tracking-wider">
            <th colspan="4" class="px-4 py-2.5 border-r border-n-slate-5 sticky left-0 z-20 bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_PERIOD') }}
            </th>
            <th colspan="3" class="px-4 py-2.5 border-r border-n-slate-5 text-center bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_EMAIL') }}
            </th>
            <th colspan="2" class="px-4 py-2.5 border-r border-n-slate-5 text-center bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_CHAT') }}
            </th>
            <th colspan="6" class="px-4 py-2.5 border-r border-n-slate-5 text-center bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_INBOUND') }}
            </th>
            <th colspan="5" class="px-4 py-2.5 border-r border-n-slate-5 text-center bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_BH') }}
            </th>
            <th colspan="3" class="px-4 py-2.5 border-r border-n-slate-5 text-center bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_NBH') }}
            </th>
            <th colspan="2" class="px-4 py-2.5 text-center bg-n-slate-3">
              {{ $t('REPORT.KPI_TABLE.GRP_ORDERS_OUTBOUND') }}
            </th>
          </tr>

          <!-- Sub Columns Headers -->
          <tr class="bg-n-slate-2 text-n-slate-12 border-b border-n-slate-4 font-medium text-[11px]">
            <!-- Period Group -->
            <th class="px-3 py-2 sticky left-0 z-20 bg-n-slate-2 border-r border-n-slate-4 min-w-[130px]">
              {{ $t('REPORT.KPI_TABLE.COL_WEEK_NO') }}
            </th>
            <th class="px-3 py-2 min-w-[100px]">{{ $t('REPORT.KPI_TABLE.COL_START_DATE') }}</th>
            <th class="px-3 py-2 min-w-[100px]">{{ $t('REPORT.KPI_TABLE.COL_END_DATE') }}</th>
            <th class="px-3 py-2 border-r border-n-slate-4 min-w-[80px] font-semibold text-right">{{ $t('REPORT.KPI_TABLE.COL_TICKETS') }}</th>

            <!-- Email Group -->
            <th class="px-3 py-2 min-w-[110px] text-right">{{ $t('REPORT.KPI_TABLE.COL_TOTAL_EMAIL') }}</th>
            <th class="px-3 py-2 min-w-[90px] text-right">{{ $t('REPORT.KPI_TABLE.COL_RESOLVED') }}</th>
            <th class="px-3 py-2 border-r border-n-slate-4 min-w-[90px] text-right">{{ $t('REPORT.KPI_TABLE.COL_EMAIL_RES_RATE') }}</th>

            <!-- Live Chat Group -->
            <th class="px-3 py-2 min-w-[90px] text-right">{{ $t('REPORT.KPI_TABLE.COL_TOTAL_CHATS') }}</th>
            <th class="px-3 py-2 border-r border-n-slate-4 min-w-[90px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_MISSED') }}</th>

            <!-- Inbound Calls Group -->
            <th class="px-3 py-2 min-w-[120px] text-right">{{ $t('REPORT.KPI_TABLE.COL_TOTAL_INBOUND') }}</th>
            <th class="px-3 py-2 min-w-[100px] text-right">{{ $t('REPORT.KPI_TABLE.COL_COMPLETED_CALLS') }}</th>
            <th class="px-3 py-2 min-w-[100px] text-right">{{ $t('REPORT.KPI_TABLE.COL_COMPLETED_PCT') }}</th>
            <th class="px-3 py-2 min-w-[90px] text-right">{{ $t('REPORT.KPI_TABLE.COL_VOICEMAILS') }}</th>
            <th class="px-3 py-2 min-w-[100px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_ABANDONED_CALLS') }}</th>
            <th class="px-3 py-2 border-r border-n-slate-4 min-w-[100px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_ABANDONED_PCT') }}</th>

            <!-- Business Hours Call Performance -->
            <th class="px-3 py-2 min-w-[130px] text-right">{{ $t('REPORT.KPI_TABLE.COL_BH_TOTAL') }}</th>
            <th class="px-3 py-2 min-w-[120px] text-right">{{ $t('REPORT.KPI_TABLE.COL_BH_ANSWERED') }}</th>
            <th class="px-3 py-2 min-w-[110px] text-right">{{ $t('REPORT.KPI_TABLE.COL_BH_COMPLETED_PCT') }}</th>
            <th class="px-3 py-2 min-w-[120px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_BH_ABANDONED') }}</th>
            <th class="px-3 py-2 border-r border-n-slate-4 min-w-[110px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_BH_ABANDONED_PCT') }}</th>

            <!-- Non-Business Hours Call Performance -->
            <th class="px-3 py-2 min-w-[130px] text-right">{{ $t('REPORT.KPI_TABLE.COL_NBH_TOTAL') }}</th>
            <th class="px-3 py-2 min-w-[110px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_NBH_ABANDONED_PCT') }}</th>
            <th class="px-3 py-2 border-r border-n-slate-4 min-w-[110px] text-right text-n-ruby-11">{{ $t('REPORT.KPI_TABLE.COL_NBH_ABANDONED') }}</th>

            <!-- TBP Orders & Outbound Calls -->
            <th class="px-3 py-2 min-w-[120px] text-right font-semibold text-n-brand">{{ $t('REPORT.KPI_TABLE.COL_TBP_ORDERS') }}</th>
            <th class="px-3 py-2 min-w-[110px] text-right">{{ $t('REPORT.KPI_TABLE.COL_CALLS_MADE') }}</th>
          </tr>
        </thead>

        <!-- Table Body -->
        <tbody class="divide-y divide-n-slate-4 text-n-slate-12">
          <tr
            v-for="(row, idx) in weeklyData"
            :key="`${row.year}-${row.week_number}-${idx}`"
            class="hover:bg-n-slate-2 transition-colors font-mono"
          >
            <!-- Period Group -->
            <td class="px-3 py-2.5 font-sans font-semibold sticky left-0 z-10 bg-n-surface-1 border-r border-n-slate-4 text-n-brand whitespace-nowrap">
              {{ row.week_label }}
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">{{ row.start_date }}</td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">{{ row.end_date }}</td>
            <td class="px-3 py-2.5 font-semibold text-right border-r border-n-slate-4">{{ row.total_tickets.toLocaleString() }}</td>

            <!-- Email Group -->
            <td class="px-3 py-2.5 text-right">{{ row.total_email_tickets.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ row.resolved_email_tickets.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-4">{{ row.email_resolution_rate }}%</td>

            <!-- Live Chat Group -->
            <td class="px-3 py-2.5 text-right">{{ row.total_live_chats.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-4 text-n-ruby-11">{{ row.missed_live_chats.toLocaleString() }}</td>

            <!-- Inbound Calls Group -->
            <td class="px-3 py-2.5 text-right">{{ row.total_inbound_calls.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ row.completed_inbound_calls.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ row.inbound_completion_rate }}%</td>
            <td class="px-3 py-2.5 text-right">{{ row.voicemails.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right text-n-ruby-11">{{ row.abandoned_calls.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-4 text-n-ruby-11">{{ row.abandoned_call_rate }}%</td>

            <!-- Business Hours Call Performance -->
            <td class="px-3 py-2.5 text-right">{{ row.total_calls_business_hours.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ row.answered_business_hours.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ row.business_hours_completion_rate }}%</td>
            <td class="px-3 py-2.5 text-right text-n-ruby-11">{{ row.abandoned_business_hours.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-4 text-n-ruby-11">{{ row.business_hours_abandonment_rate }}%</td>

            <!-- Non-Business Hours Call Performance -->
            <td class="px-3 py-2.5 text-right">{{ row.total_calls_non_business_hours.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right text-n-ruby-11">{{ row.non_business_hours_abandonment_rate }}%</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-4 text-n-ruby-11">{{ row.abandoned_non_business_hours.toLocaleString() }}</td>

            <!-- TBP Orders & Outbound Calls -->
            <td class="px-3 py-2.5 text-right font-semibold text-n-brand">{{ row.total_tbp_orders.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ row.total_calls_made.toLocaleString() }}</td>
          </tr>
        </tbody>

        <!-- Totals Summary Row -->
        <tfoot v-if="totals" class="bg-n-slate-3 font-semibold text-n-slate-12 border-t-2 border-n-slate-5 font-mono">
          <tr>
            <td class="px-3 py-2.5 sticky left-0 z-10 bg-n-slate-3 border-r border-n-slate-5 font-sans font-bold">
              {{ $t('REPORT.KPI_TABLE.TOTAL') }}
            </td>
            <td class="px-3 py-2.5 font-sans text-n-slate-10" colspan="2">—</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-5">{{ totals.totalTickets.toLocaleString() }}</td>

            <!-- Email -->
            <td class="px-3 py-2.5 text-right">{{ totals.totalEmailTickets.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ totals.resolvedEmailTickets.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-5">{{ totals.emailResolutionRate }}%</td>

            <!-- Live Chat -->
            <td class="px-3 py-2.5 text-right">{{ totals.totalLiveChats.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-5 text-n-ruby-11">{{ totals.missedLiveChats.toLocaleString() }}</td>

            <!-- Inbound Calls -->
            <td class="px-3 py-2.5 text-right">{{ totals.totalInboundCalls.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ totals.completedInboundCalls.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ totals.inboundCompletionRate }}%</td>
            <td class="px-3 py-2.5 text-right">{{ totals.voicemails.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right text-n-ruby-11">{{ totals.abandonedCalls.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-5 text-n-ruby-11">{{ totals.abandonedCallRate }}%</td>

            <!-- Business Hours -->
            <td class="px-3 py-2.5 text-right">{{ totals.totalCallsBH.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ totals.answeredBH.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ totals.bhCompletionRate }}%</td>
            <td class="px-3 py-2.5 text-right text-n-ruby-11">{{ totals.abandonedBH.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-5 text-n-ruby-11">{{ totals.bhAbandonmentRate }}%</td>

            <!-- Non-Business Hours -->
            <td class="px-3 py-2.5 text-right">{{ totals.totalCallsNBH.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right text-n-ruby-11">{{ totals.nbhAbandonmentRate }}%</td>
            <td class="px-3 py-2.5 text-right border-r border-n-slate-5 text-n-ruby-11">{{ totals.abandonedNBH.toLocaleString() }}</td>

            <!-- TBP & Outbound -->
            <td class="px-3 py-2.5 text-right text-n-brand">{{ totals.totalTbpOrders.toLocaleString() }}</td>
            <td class="px-3 py-2.5 text-right">{{ totals.totalCallsMade.toLocaleString() }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>
